import { cs } from '../../../lib/currency';
/**
 * Move Enrollment Dialog (iter269 — extracted from Enrollments.js).
 *
 * Reassigns an existing enrollment to a new program/class with an
 * optional fee change. All business logic (program/class filtering,
 * submit handler) is owned by the parent and passed down.
 *
 * Props:
 *   - open                       (bool)
 *   - onOpenChange               ((bool) => void)
 *   - selectedEnrollment         (object | null)
 *   - programs                   (Array<Program>)
 *   - moveData                   (state object — see Enrollments.js)
 *   - setMoveData                ((data) => void)
 *   - moveLoading                (bool)
 *   - onProgramChange            ((programId) => void)  — auto-populates default fee
 *   - getMoveFilteredClasses     (() => Array<Class>)
 *   - onConfirm                  (() => Promise<void>)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  ArrowRightLeft, BookOpen, Calendar, DollarSign,
} from 'lucide-react';

export default function MoveEnrollmentDialog({
  open,
  onOpenChange,
  selectedEnrollment,
  programs,
  moveData,
  setMoveData,
  moveLoading,
  onProgramChange,
  getMoveFilteredClasses,
  onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-500" />
            Move Enrollment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Current Enrollment Info */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
            <p className="text-sm text-slate-500">
              Current Program: {selectedEnrollment?.program_name}
            </p>
            {selectedEnrollment?.class_names && selectedEnrollment.class_names.length > 0 && (
              <p className="text-sm text-slate-500">
                Current Class: {selectedEnrollment.class_names.join(', ')}
              </p>
            )}
            {selectedEnrollment?.monthly_fee && (
              <p className="text-sm text-slate-500">
                Current Fee: {cs()}{selectedEnrollment.monthly_fee.toFixed(2)}/mo
              </p>
            )}
          </div>

          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Moving an enrollment will update the player's program/class assignment and optionally adjust the monthly fee. The enrollment history will be preserved.
            </p>
          </div>

          {/* New Program Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-500" />
              New Program *
            </Label>
            <Select value={moveData.new_program_id} onValueChange={onProgramChange}>
              <SelectTrigger data-testid="move-program-select">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-48 overflow-y-auto z-[100]" sideOffset={4}>
                {programs.filter(p => p.is_active !== false).map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name} - {cs()}{program.price}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* New Class Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-500" />
              New Class *
            </Label>
            <Select
              value={moveData.new_class_id}
              onValueChange={(value) => setMoveData({ ...moveData, new_class_id: value })}
              disabled={!moveData.new_program_id}
            >
              <SelectTrigger data-testid="move-class-select">
                <SelectValue placeholder={moveData.new_program_id ? 'Select a class' : 'Select program first'} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-48 overflow-y-auto z-[100]" sideOffset={4}>
                {getMoveFilteredClasses().length === 0 ? (
                  <div className="px-2 py-3 text-center text-sm text-slate-500">
                    No classes available for this program
                  </div>
                ) : (
                  getMoveFilteredClasses().map((classItem) => (
                    <SelectItem key={classItem.id} value={classItem.id}>
                      {classItem.name} - {classItem.schedule_day} {classItem.schedule_time}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* New Monthly Fee */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-amber-500" />
              New Monthly Fee ($)
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={moveData.new_monthly_fee}
              onChange={(e) => setMoveData({ ...moveData, new_monthly_fee: e.target.value })}
              placeholder="Leave empty to use program default"
              data-testid="move-fee-input"
            />
            {moveData.new_program_id && selectedEnrollment?.monthly_fee && (
              <div className="text-xs text-slate-500">
                {parseFloat(moveData.new_monthly_fee || 0) !== selectedEnrollment.monthly_fee ? (
                  <span className="text-amber-600">
                    Fee change: {cs()}{selectedEnrollment.monthly_fee.toFixed(2)} → {cs()}{parseFloat(moveData.new_monthly_fee || 0).toFixed(2)}
                  </span>
                ) : (
                  <span>No fee change</span>
                )}
              </div>
            )}
          </div>

          {/* Effective Date */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              Effective Date
            </Label>
            <Input
              type="date"
              value={moveData.effective_date}
              onChange={(e) => setMoveData({ ...moveData, effective_date: e.target.value })}
              data-testid="move-effective-date-input"
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason (Optional)</Label>
            <Textarea
              value={moveData.reason}
              onChange={(e) => setMoveData({ ...moveData, reason: e.target.value })}
              placeholder="Reason for moving this enrollment..."
              data-testid="move-reason-input"
            />
          </div>

          {/* Notify Parent Checkbox */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="notify-parent"
              checked={moveData.notify_parent}
              onChange={(e) => setMoveData({ ...moveData, notify_parent: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="move-notify-checkbox"
            />
            <Label htmlFor="notify-parent" className="text-sm font-normal cursor-pointer">
              Send email notification to parent
            </Label>
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={onConfirm}
              disabled={!moveData.new_program_id || !moveData.new_class_id || moveLoading}
              data-testid="confirm-move-btn"
            >
              {moveLoading ? (
                <span className="animate-spin mr-1">⏳</span>
              ) : (
                <ArrowRightLeft className="h-4 w-4 mr-1" />
              )}
              Move Enrollment
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
